import React from 'react';
import { Link } from 'react-router-dom';
import { Edit, Trash2, Users as UsersIcon, Plus, Milestone as MilestoneIcon } from 'lucide-react';
import Button from './Button';
import Badge from './Badge';
import ProjectRoleForm from './ProjectRoleForm';
import { Project } from '../types/project';
import { ProjectRole } from '../types/role';
import { User } from '../types/user';

interface ProjectMetrics {
  totalTasks: number;
  completedTasks: number;
  overdueTasks: number;
  completionRate: number;
  milestoneCount: number;
}

interface ProjectCardProps {
  project: Project;
  metrics: ProjectMetrics;
  projectRoles: ProjectRole[];
  users: User[];
  managingRolesFor: string | null;
  showRoleForm: boolean;
  editingRole: ProjectRole | undefined;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
  onManageRoles: (projectId: string) => void;
  onAddTask?: (projectId: string) => void;
  onAddMilestone?: (projectId: string) => void;
  onViewTasks?: (projectId: string) => void;
  onViewMilestones?: (projectId: string) => void;
  onRoleFormToggle: () => void;
  onRoleSuccess: () => void;
  onRoleCancel: () => void;
  onEditRole: (role: ProjectRole) => void;
  onCloseRoles: () => void;
  getManagerName: (managerId?: string) => string;
  getProgrammeName: (programmeId?: string) => string;
  getUserName: (userId: string) => string;
  canEdit: boolean;
  canManageRoles?: boolean;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  metrics,
  projectRoles,
  users: _users, // Prefix with underscore to indicate intentionally unused
  managingRolesFor,
  showRoleForm,
  editingRole,
  onEdit,
  onDelete,
  onManageRoles,
  onAddTask,
  onAddMilestone,
  onViewTasks,
  onViewMilestones,
  onRoleFormToggle,
  onRoleSuccess,
  onRoleCancel,
  onEditRole,
  onCloseRoles,
  getManagerName,
  getProgrammeName,
  getUserName,
  canEdit,
  canManageRoles = false,
}) => {
  const projectRolesList = projectRoles.filter((r) => r.project_id === project.id);

  return (
    <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/50 rounded-xl p-6 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
      {/* Header */}
      <div className="mb-4 pb-4 border-b-2 border-gray-100 dark:border-gray-700">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 m-0">
          <Link
            to={`/projects/${project.id}`}
            className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            {project.name}
          </Link>
        </h3>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          ID: {project.id}
        </span>
      </div>

      {/* Description and Metadata */}
      <div className="text-gray-700 dark:text-gray-300">
        {project.description ? (
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            {project.description}
          </p>
        ) : (
          <p className="italic text-gray-400 dark:text-gray-500 mb-4">
            No description provided
          </p>
        )}
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Created: {new Date(project.created_at).toLocaleDateString()}
        </p>
        {project.manager_id && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manager: {getManagerName(project.manager_id)}
          </p>
        )}
        {project.programme_id && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Programme: {getProgrammeName(project.programme_id)}
          </p>
        )}
        {project.priority && (
          <Badge priority={project.priority as any} size="md">
            {project.priority}
          </Badge>
        )}
      </div>

      {/* Project Completion Metrics */}
      <div className="bg-gradient-to-br from-white/90 to-gray-50/90 dark:from-gray-800/90 dark:to-gray-900/90 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 p-4 rounded-lg mb-4">
        {/* Top Row: Total Tasks, Overdue, and Milestones */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="text-center">
            <div className="text-xl font-semibold text-primary-600 dark:text-primary-400">
              {metrics.totalTasks}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Total Tasks
            </div>
          </div>
          <div className="text-center">
            <div
              className={`text-xl font-semibold ${
                metrics.overdueTasks > 0
                  ? "text-red-600 dark:text-red-400"
                  : "text-green-600 dark:text-green-400"
              }`}
            >
              {metrics.overdueTasks}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Overdue
            </div>
          </div>
          <div className="text-center">
            <div className="text-xl font-semibold text-accent-600 dark:text-accent-400">
              {metrics.milestoneCount}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Milestones
            </div>
          </div>
        </div>

        {/* Bottom Row: Completion */}
        <div className="text-center">
          <div className="text-xl font-semibold text-green-600 dark:text-green-400">
            {metrics.completionRate}%
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            Completion
          </div>
          <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mt-2">
            <div
              className={`h-full transition-all duration-300 rounded-full ${
                metrics.completionRate === 100
                  ? "bg-green-500"
                  : "bg-primary-500"
              }`}
              style={{ width: `${metrics.completionRate}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Team Roles Section */}
      {managingRolesFor === project.id && (
        <div className="bg-gradient-to-br from-white/90 to-gray-50/90 dark:from-gray-800/90 dark:to-gray-900/90 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 p-4 rounded-lg mb-4">
          <div className="flex justify-between items-center mb-4">
            <h4 className="m-0 text-gray-900 dark:text-gray-100">Team Roles</h4>
            <div className="flex gap-2">
              <Button
                onClick={onRoleFormToggle}
                variant="primary"
                size="sm"
              >
                {showRoleForm ? "Cancel" : "+ Assign Role"}
              </Button>
              <Button onClick={onCloseRoles} variant="outline" size="sm">
                Close
              </Button>
            </div>
          </div>

          {showRoleForm && (
            <div className="mb-4">
              <ProjectRoleForm
                projectId={project.id}
                role={editingRole}
                onSuccess={onRoleSuccess}
                onCancel={onRoleCancel}
              />
            </div>
          )}

          {/* Roles List */}
          {projectRolesList.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 italic text-center p-4">
              No team roles assigned yet
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {projectRolesList.map((role) => (
                <div
                  key={role.id}
                  className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 flex justify-between items-center p-2 rounded"
                >
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {getUserName(role.user_id)}
                    </div>
                    <div className="text-sm text-primary-600 dark:text-primary-400">
                      {role.role}
                    </div>
                  </div>
                  <Button
                    onClick={() => onEditRole(role)}
                    variant="outline"
                    size="sm"
                  >
                    Edit
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex gap-2 flex-wrap">
        {canEdit && (
          <>
            <Button
              onClick={() => onEdit(project)}
              variant="primary"
              size="sm"
              leftIcon={Edit}
            >
              Edit
            </Button>
            {onAddTask && (
              <Button
                onClick={() => onAddTask(project.id)}
                variant="success"
                size="sm"
                leftIcon={Plus}
                title="Add Task"
              >
                Add Task
              </Button>
            )}
            {onAddMilestone && (
              <Button
                onClick={() => onAddMilestone(project.id)}
                variant="success"
                size="sm"
                leftIcon={MilestoneIcon}
                title="Add Milestone"
              >
                Add Milestone
              </Button>
            )}
          </>
        )}
        {onViewTasks && (
          <Button
            onClick={() => onViewTasks(project.id)}
            variant="outline"
            size="sm"
            title={`View all tasks (${metrics.totalTasks})`}
          >
            View Tasks ({metrics.totalTasks})
          </Button>
        )}
        {onViewMilestones && (
          <Button
            onClick={() => onViewMilestones(project.id)}
            variant="outline"
            size="sm"
            title={`View all milestones (${metrics.milestoneCount})`}
          >
            View Milestones ({metrics.milestoneCount})
          </Button>
        )}
        {canManageRoles && (
          <Button
            onClick={() => onManageRoles(project.id)}
            variant="secondary"
            size="sm"
            leftIcon={UsersIcon}
          >
            {managingRolesFor === project.id ? "Hide Team" : "Manage Team"}
          </Button>
        )}
        {canEdit && (
          <Button
            onClick={() => onDelete(project)}
            variant="danger"
            size="sm"
            leftIcon={Trash2}
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProjectCard;
